import { CheckCircle, Error } from '@mui/icons-material'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Stack,
    Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useContext, useEffect, useState } from 'react'
import { Field, FormRenderProps, useForm } from 'react-final-form'
import { DataPanel } from '../../../components/DataPanel'
import { CustomValidationContext } from '../../../components/MultistepForm/MultistepForm'
import MultistepFormField, {
    singleSelectCheckboxUtil,
} from '../../../components/MultistepForm/MultistepFormField'
import {
    getBlockchainIdentities,
    validateSubscriberForNode,
} from '../../../services/blockchainService'
import {
    BlockchainIdentity,
    BlockchainIdentityResponse,
} from '../../../services/models/blockchain-identity'

const RELATIONSHIP_FIELDS = [
    'coverage-otherRelationshipOther',
    'coverage-otherRelationshipDependent',
    'coverage-otherRelationshipSpouse',
    'coverage-otherRelationshipSelf',
]

const SUBSCRIBER_FIELDS = [
    'coverage-subscriberFirstName',
    'coverage-subscriberLastName',
    'coverage-subscriberDOB',
    'coverage-subscriberGenderMale',
    'coverage-subscriberGenderFemale',
    'coverage-subscriberIdentifier',
    'coverage-subscriberEmployerName',
    'coverage-subscriberPlanOrGroupNumber',
    'coverage-subscriberMiddleInitial',
    'coverage-subscriberSuffix',
    'coverage-subscriberAddress',
    'coverage-subscriberCity',
    'coverage-subscriberState',
    'coverage-subscriberZipCode',
    'coverage-subscriberId',
]

export const getSubscriberInformation = (form: FormRenderProps['form']) => {
    const { values } = form.getState()

    return {
        firstName: values['coverage-subscriberFirstName'],
        lastName: values['coverage-subscriberLastName'],
        dateOfBirth: dayjs(values['coverage-subscriberDOB']).format('YYYY-MM-DD'),
        gender: values['coverage-subscriberGenderMale']
            ? 'M'
            : values['coverage-subscriberGenderFemale']
            ? 'F'
            : undefined,
        ssn: values['coverage-subscriberIdentifier'],
        employerName: values['coverage-subscriberEmployerName'],
        planNumber: values['coverage-subscriberPlanOrGroupNumber'],
        subscriberId: values['coverage-subscriberId'],
        // middleInitial and suffix are optional
        middleInitial: values['coverage-subscriberMiddleInitial']?.length
            ? values['coverage-subscriberMiddleInitial']
            : undefined,
        suffix: values['coverage-subscriberSuffix']?.length
            ? values['coverage-subscriberSuffix']
            : undefined,
        address: {
            addressLine1: values['coverage-subscriberAddress'],
            city: values['coverage-subscriberCity'],
            state: values['coverage-subscriberState'],
            zipCode: values['coverage-subscriberZipCode'],
        },
    }
}

interface PayerSearchModalProps {
    open: boolean
    loading: boolean
    data?: BlockchainIdentityResponse
    onCancel: () => void
    onSubmit: (selectedPayer: BlockchainIdentity) => void
}

const PayerSearchModal = (props: PayerSearchModalProps) => {
    const [selectedPayer, setSelectedPayer] = useState<BlockchainIdentity>()

    const renderContents = () => {
        if (props.loading) return <CircularProgress />

        return (
            <Stack divider={<Divider />} spacing={2}>
                {props.data?.map((payer, i) => {
                    const key = `${i}-${payer.orgName}`
                    const isSelected = selectedPayer && `${i}-${selectedPayer.orgName}` === key
                    return (
                        <Box
                            key={key}
                            sx={{
                                backgroundColor: isSelected ? 'ButtonFace' : undefined,
                                borderRadius: 2,
                                padding: 2,
                                cursor: 'pointer',
                            }}
                            onClick={() => setSelectedPayer(payer)}
                        >
                            <Typography>{payer.orgName}</Typography>
                        </Box>
                    )
                })}
            </Stack>
        )
    }

    return (
        <Dialog open={props.open}>
            <DialogTitle>Search Payers in Network</DialogTitle>
            <DialogContent>{renderContents()}</DialogContent>
            <DialogActions>
                <Button
                    onClick={() => {
                        props.onCancel()
                        setSelectedPayer(undefined)
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={() => {
                        selectedPayer && props.onSubmit(selectedPayer)
                        setSelectedPayer(undefined)
                    }}
                    disabled={selectedPayer === undefined}
                >
                    Done
                </Button>
            </DialogActions>
        </Dialog>
    )
}

// Work around for custom validation weird state side effects
// We should probably pull stuff like this into a parent / global state in the future
let payerValidationStatus: 'OK' | 'LOADING' | 'INVALID' | undefined

const CoverageStep = () => {
    const form = useForm()
    const customValidation = useContext(CustomValidationContext)
    const [payerSearchOpen, setPayerSearchOpen] = useState(false)
    const [payerSearchLoading, setPayerSearchLoading] = useState(false)
    const [payerSearchData, setPayerSearchData] = useState<BlockchainIdentityResponse>([])
    const [payerValidationStatusIntState, setPayerValidationStatusIntState] = useState<
        'OK' | 'LOADING' | 'INVALID'
    >()
    const [payerDid, setPayerDid] = useState<string>()
    const [subscriberInternalState, setSubscriberInternalState] = useState<any>({})

    const hasOtherCoverage = form.getState().values['coverage-otherCoverageYes']

    useEffect(() => {
        const isSubscriberValid = form.getState().values['coverage-subscriberIsValid']
        if (isSubscriberValid !== undefined) {
            const status = isSubscriberValid ? 'OK' : 'INVALID'
            payerValidationStatus = status
            setPayerValidationStatusIntState(status)
        }
    }, [form.getState().values['coverage-subscriberIsValid']])

    useEffect(() => {
        setPayerDid(form.getState().values['coverage-payerDid'])
    }, [form.getState().values['coverage-payerDid']])

    useEffect(() => {
        const formValues = form.getState().values

        const fieldsHaveChanged = SUBSCRIBER_FIELDS.reduce(
            (acc, fieldName) =>
                acc
                    ? acc
                    : (subscriberInternalState[fieldName] ?? null) !==
                      (formValues[fieldName] ?? null),
            false,
        )
        if (fieldsHaveChanged) {
            setPayerValidationStatusIntState(undefined)
            payerValidationStatus = undefined
        }

        setSubscriberInternalState(
            SUBSCRIBER_FIELDS.reduce(
                (acc, fieldName) => ({ ...acc, [fieldName]: formValues[fieldName] ?? null }),
                {},
            ),
        )
    }, [form.getState()])

    const onPayerSearch = async () => {
        setPayerSearchOpen(true)
        setPayerSearchLoading(true)
        const registeredPayerIdentities = await getBlockchainIdentities('payer')
        setPayerSearchData(registeredPayerIdentities.data)
        setPayerSearchLoading(false)
    }

    const onSelectPayer = (identity: BlockchainIdentity) => {
        const change = form.change
        setPayerSearchOpen(false)
        change('coverage-payerDid', identity.did)
        change('coverage-payerOrgName', identity.orgName)
        change('coverage-payerInterchangeId', identity.interchangeId)
        change('coverage-payerEtin', identity.etin)
        change('coverage-payerPhoneNumber', identity.phoneNumber)
        change('coverage-payerPrefix', identity.prefix)
        change('coverage-payerFirstName', identity.firstName)
        change('coverage-payerLastName', identity.lastName)
        change('coverage-payerAddress', identity.address.addressLine1)
        change('coverage-payerState', identity.address.state)
        change('coverage-payerCity', identity.address.city)
        change('coverage-payerZipCode', identity.address.zipCode)
    }

    // TODO: pull payer validation status to somewhere else where not so hacky
    // if we are planning on doing something like this again in the future
    const onValidateSubscriber = async () => {
        const _payerDid = form.getState().values['coverage-payerDid']
        if (!_payerDid) return false
        setPayerValidationStatusIntState('LOADING')
        payerValidationStatus = 'LOADING'
        try {
            const payload = getSubscriberInformation(form)
            const {
                data: {
                    data: { payload: isValid },
                },
            } = await validateSubscriberForNode(payload as any, _payerDid)
            const status = isValid ? 'OK' : 'INVALID'
            setPayerValidationStatusIntState(status)
            payerValidationStatus = status
            return isValid
        } catch (err) {
            setPayerValidationStatusIntState('INVALID')
            payerValidationStatus = 'INVALID'
            return false
        }
    }
    customValidation('coverage-subscriberIsValid', onValidateSubscriber)

    const primarySubscriberInfoPanelWidget = () => {
        const status =
            payerValidationStatusIntState === 'LOADING' ? 'LOADING' : payerValidationStatus
        switch (status) {
            case 'OK':
                return <CheckCircle color="success" />
            case 'INVALID':
                return <Error color="error" />
            case 'LOADING':
                return <CircularProgress />
        }
    }
    const primarySubscriberInfoPanelStyle = () => {
        switch (payerValidationStatus) {
            case 'OK':
                return { border: '1px solid green' }
            case 'INVALID':
                return { border: '1px solid red' }
        }
    }

    return (
        <Box>
            <PayerSearchModal
                open={payerSearchOpen}
                loading={payerSearchLoading}
                data={payerSearchData}
                onCancel={() => setPayerSearchOpen(false)}
                onSubmit={onSelectPayer}
            />
            <Field name="coverage-subscriberIsValid" render={() => <></>} />
            <Field name="coverage-payerDid" render={() => <></>} />
            <Field name="coverage-payerEtin" render={() => <></>} />
            <Field name="coverage-payerInterchangeId" render={() => <></>} />
            <Field name="coverage-payerPhoneNumber" render={() => <></>} />
            <Field name="coverage-payerPrefix" render={() => <></>} />
            <Field name="coverage-payerFirstName" render={() => <></>} />
            <Field name="coverage-payerLastName" render={() => <></>} />

            <Grid container spacing={2} sx={{ alignItems: 'self-end' }}>
                <Grid item xs={6}>
                    <DataPanel
                        title="Primary Payer Information"
                        widget={<Button>View details</Button>}
                        content={
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <MultistepFormField
                                        name="coverage-payerOrgName"
                                        label="Name"
                                        inputProps={{ disabled: true }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Button sx={{ width: '100%' }} onClick={onPayerSearch}>
                                        Search
                                    </Button>
                                </Grid>
                                <Grid item xs={6}>
                                    <MultistepFormField
                                        name="coverage-payerAddress"
                                        label="Address"
                                        inputProps={{ disabled: true }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <MultistepFormField
                                        name="coverage-payerState"
                                        label="State"
                                        inputProps={{ disabled: true }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <MultistepFormField
                                        name="coverage-payerCity"
                                        label="City"
                                        inputProps={{ disabled: true }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <MultistepFormField
                                        name="coverage-payerZipCode"
                                        label="Zip Code"
                                        inputProps={{ type: 'number', disabled: true }}
                                    />
                                </Grid>
                            </Grid>
                        }
                    />
                </Grid>
                <Grid item xs={6}>
                    <DataPanel
                        title="Header Information"
                        content={
                            <Box>
                                <Typography>Type of Transaction</Typography>
                                <Stack>
                                    <MultistepFormField
                                        type="checkbox"
                                        label="Statement of Actual Services"
                                        name="coverage-statementOfActualServices"
                                        inputProps={{ disabled: true }}
                                    />
                                    <MultistepFormField
                                        type="checkbox"
                                        label="Request for Predetermination/Preauthorization"
                                        name="coverage-requestForPreAuth"
                                        inputProps={{ disabled: true }}
                                    />
                                    <MultistepFormField
                                        type="checkbox"
                                        label="EPSDT / Title XIX"
                                        name="coverage-epsdtTitleXIX"
                                        inputProps={{ disabled: true }}
                                    />
                                </Stack>
                            </Box>
                        }
                    />
                </Grid>
                <Grid item xs={12}>
                    <DataPanel
                        title="Primary Subscriber Information"
                        style={primarySubscriberInfoPanelStyle()}
                        widget={primarySubscriberInfoPanelWidget()}
                        content={
                            <Grid container spacing={2}>
                                <Grid item xs={3}>
                                    <MultistepFormField
                                        label="Last Name"
                                        name="coverage-subscriberLastName"
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <MultistepFormField
                                        label="First Name"
                                        name="coverage-subscriberFirstName"
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <MultistepFormField
                                        label="Address"
                                        name="coverage-subscriberAddress"
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <MultistepFormField
                                        label="State"
                                        name="coverage-subscriberState"
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <MultistepFormField
                                        label="Middle Initial"
                                        name="coverage-subscriberMiddleInitial"
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <MultistepFormField
                                        label="Suffix"
                                        name="coverage-subscriberSuffix"
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <MultistepFormField
                                        label="City"
                                        name="coverage-subscriberCity"
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <MultistepFormField
                                        label="Zip Code"
                                        name="coverage-subscriberZipCode"
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <MultistepFormField
                                        type="date"
                                        label="Date of Birth"
                                        name="coverage-subscriberDOB"
                                        inputProps={{ disableFuture: true }}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <Stack direction="row">
                                        <MultistepFormField
                                            type="checkbox"
                                            label="Male"
                                            name="coverage-subscriberGenderMale"
                                            inputProps={{
                                                onChange: () =>
                                                    singleSelectCheckboxUtil(
                                                        form,
                                                        ['coverage-subscriberGenderFemale'],
                                                        'coverage-subscriberGenderMale',
                                                    ),
                                            }}
                                        />
                                        <MultistepFormField
                                            type="checkbox"
                                            label="Female"
                                            name="coverage-subscriberGenderFemale"
                                            inputProps={{
                                                onChange: () =>
                                                    singleSelectCheckboxUtil(
                                                        form,
                                                        ['coverage-subscriberGenderMale'],
                                                        'coverage-subscriberGenderFemale',
                                                    ),
                                            }}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={6} />
                                <Grid item xs={3}>
                                    <MultistepFormField
                                        label="Subscriber Identifier (SSN or ID*)"
                                        name="coverage-subscriberIdentifier"
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <MultistepFormField
                                        label="Plan / Group Number"
                                        name="coverage-subscriberPlanOrGroupNumber"
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <MultistepFormField
                                        label="Subscriber Id"
                                        name="coverage-subscriberId"
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <MultistepFormField
                                        label="Employer Name"
                                        name="coverage-subscriberEmployerName"
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'right',
                                        }}
                                    >
                                        <Button
                                            disabled={
                                                !payerDid?.length ||
                                                payerValidationStatus === 'LOADING'
                                            }
                                            onClick={onValidateSubscriber}
                                        >
                                            Validate
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        }
                    />
                </Grid>
                <Grid item xs={12}>
                    <DataPanel
                        title="Other Coverage"
                        content={
                            <Stack direction="row" spacing={2}>
                                <Grid container spacing={2} sx={{ alignItems: 'self-end' }}>
                                    <Grid item xs={12}>
                                        <Typography>Other Dental or Medical Coverage?</Typography>
                                        <MultistepFormField
                                            type="checkbox"
                                            label="No"
                                            name="coverage-otherCoverageNo"
                                            inputProps={{
                                                onChange: () => {
                                                    form.change('coverage-otherCoverageNo', true)
                                                    form.change('coverage-otherCoverageYes', false)
                                                },
                                            }}
                                        />
                                        <MultistepFormField
                                            type="checkbox"
                                            label="Yes"
                                            name="coverage-otherCoverageYes"
                                            inputProps={{
                                                disabled: true, // TODO
                                                onChange: () => {
                                                    form.change('coverage-otherCoverageYes', true)
                                                    form.change('coverage-otherCoverageNo', false)
                                                },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography>Subscriber Name</Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <MultistepFormField
                                                    inputProps={{ disabled: !hasOtherCoverage }}
                                                    label="Last Name"
                                                    name="coverage-otherLastName"
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <MultistepFormField
                                                    inputProps={{ disabled: !hasOtherCoverage }}
                                                    label="First Name"
                                                    name="coverage-otherFirstName"
                                                />
                                            </Grid>
                                            <Grid item xs={3}>
                                                <MultistepFormField
                                                    inputProps={{ disabled: !hasOtherCoverage }}
                                                    label="Middle Initial"
                                                    name="coverage-otherMiddleInitial"
                                                />
                                            </Grid>
                                            <Grid item xs={3}>
                                                <MultistepFormField
                                                    inputProps={{ disabled: !hasOtherCoverage }}
                                                    label="Suffix"
                                                    name="coverage-otherSuffix"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography>Date of Birth</Typography>
                                        <MultistepFormField
                                            type="date"
                                            name="coverage-otherDOB"
                                            inputProps={{
                                                disabled: !hasOtherCoverage,
                                                disableFuture: true,
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography>Gender</Typography>
                                        <Stack direction="row">
                                            <MultistepFormField
                                                type="checkbox"
                                                label="Male"
                                                name="coverage-otherMale"
                                                inputProps={{
                                                    disabled: !hasOtherCoverage,
                                                    onChange: () => {
                                                        form.change('coverage-otherMale', true)
                                                        form.change('coverage-otherFemale', false)
                                                    },
                                                }}
                                            />
                                            <MultistepFormField
                                                type="checkbox"
                                                label="Female"
                                                name="coverage-otherFemale"
                                                inputProps={{
                                                    disabled: !hasOtherCoverage,
                                                    onChange: () => {
                                                        form.change('coverage-otherFemale', true)
                                                        form.change('coverage-otherMale', false)
                                                    },
                                                }}
                                            />
                                        </Stack>
                                    </Grid>
                                </Grid>
                                <Grid container spacing={2} sx={{ alignItems: 'self-end' }}>
                                    <Grid item xs={6}>
                                        <Typography>Subscriber Identifier (SSN or ID#)</Typography>
                                        <MultistepFormField
                                            name="coverage-otherSubscriberId"
                                            inputProps={{ disabled: !hasOtherCoverage }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography>Plan / Group Number</Typography>
                                        <MultistepFormField
                                            name="coverage-otherPlanNumber"
                                            inputProps={{ disabled: !hasOtherCoverage }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography>Relationship to Primary Subscriber</Typography>
                                        <Stack>
                                            <MultistepFormField
                                                type="checkbox"
                                                label="Self"
                                                name="coverage-otherRelationshipSelf"
                                                inputProps={{
                                                    disabled: !hasOtherCoverage,
                                                    onChange: () =>
                                                        singleSelectCheckboxUtil(
                                                            form,
                                                            RELATIONSHIP_FIELDS,
                                                            'coverage-otherRelationshipSelf',
                                                        ),
                                                }}
                                            />
                                            <MultistepFormField
                                                type="checkbox"
                                                label="Spouse"
                                                name="coverage-otherRelationshipSpouse"
                                                inputProps={{
                                                    disabled: !hasOtherCoverage,
                                                    onChange: () =>
                                                        singleSelectCheckboxUtil(
                                                            form,
                                                            RELATIONSHIP_FIELDS,
                                                            'coverage-otherRelationshipSpouse',
                                                        ),
                                                }}
                                            />
                                            <MultistepFormField
                                                type="checkbox"
                                                label="Dependent"
                                                name="coverage-otherRelationshipDependent"
                                                inputProps={{
                                                    disabled: !hasOtherCoverage,
                                                    onChange: () =>
                                                        singleSelectCheckboxUtil(
                                                            form,
                                                            RELATIONSHIP_FIELDS,
                                                            'coverage-otherRelationshipDependent',
                                                        ),
                                                }}
                                            />
                                            <MultistepFormField
                                                type="checkbox"
                                                label="Other"
                                                name="coverage-otherRelationshipOther"
                                                inputProps={{
                                                    disabled: !hasOtherCoverage,
                                                    onChange: () =>
                                                        singleSelectCheckboxUtil(
                                                            form,
                                                            RELATIONSHIP_FIELDS,
                                                            'coverage-otherRelationshipOther',
                                                        ),
                                                }}
                                            />
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography>Other Carrier Information</Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <MultistepFormField
                                                    label="Other Carrier Name"
                                                    name="coverage-otherCarrierName"
                                                    inputProps={{ disabled: !hasOtherCoverage }}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <MultistepFormField
                                                    label="Address"
                                                    name="coverage-otherCarrierAddress"
                                                    inputProps={{ disabled: !hasOtherCoverage }}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <MultistepFormField
                                                    label="City"
                                                    name="coverage-otherCarrierCity"
                                                    inputProps={{ disabled: !hasOtherCoverage }}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <MultistepFormField
                                                    label="State"
                                                    name="coverage-otherCarrierState"
                                                    inputProps={{ disabled: !hasOtherCoverage }}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <MultistepFormField
                                                    label="Zip Code"
                                                    name="coverage-otherCarrierZipCode"
                                                    inputProps={{ disabled: !hasOtherCoverage }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Stack>
                        }
                    />
                </Grid>
            </Grid>
        </Box>
    )
}

export default CoverageStep
